/**
 * `.adamsamples` zip — full host sample library (folders + imported PCM).
 *
 * Layout:
 * ```
 * metadata.json   # { format: "adamsamples", version, savedAt, generator }
 * folders.json    # SampleFolderMeta[]
 * manifest.json   # SampleAssetMeta[]
 * samples/{id}.wav
 * ```
 */
import { strFromU8, strToU8, unzipSync, zipSync } from "fflate";
import type { SampleAssetMeta, SampleFolderMeta } from "../../../../src/lib/audio";
import {
  ADAM_SAMPLES_AUDIO_DIR,
  ADAM_SAMPLES_FILE_EXTENSION,
  ADAM_SAMPLES_FOLDERS_JSON_PATH,
  ADAM_SAMPLES_FORMAT,
  ADAM_SAMPLES_MANIFEST_JSON_PATH,
  ADAM_SAMPLES_METADATA_JSON_PATH,
  ADAM_SAMPLES_MIME_TYPE,
  ADAM_SAMPLES_PACKAGE_VERSION,
} from "./fileTypes";
import { decodeWavToMono, encodeWavMono16 } from "./wavCodec";

export { ADAM_SAMPLES_FILE_EXTENSION, ADAM_SAMPLES_MIME_TYPE };

export type AdamSamplesMetadataJson = {
  format: typeof ADAM_SAMPLES_FORMAT;
  version: typeof ADAM_SAMPLES_PACKAGE_VERSION;
  savedAt: string;
  generator: "adam";
  sampleCount: number;
  folderCount: number;
};

export type AdamSamplesPackedSample = {
  meta: SampleAssetMeta;
  pcm: Float32Array;
};

export type AdamSamplesPackage = {
  metadata: AdamSamplesMetadataJson;
  folders: SampleFolderMeta[];
  samples: AdamSamplesPackedSample[];
};

function stableJsonStringify(value: unknown): string {
  return `${JSON.stringify(sortJson(value))}\n`;
}

function sortJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortJson);
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, nested]) => [key, sortJson(nested)]),
    );
  }
  return value;
}

function audioAssetPath(sampleId: string): string {
  return `${ADAM_SAMPLES_AUDIO_DIR}${sampleId}.wav`;
}

/** Pack folders + samples into `.adamsamples` zip bytes. */
export function serializeAdamSamplesPackage(options: {
  folders: SampleFolderMeta[];
  samples: AdamSamplesPackedSample[];
}): Uint8Array {
  const folders = options.folders ?? [];
  const samples = options.samples ?? [];
  const metadata: AdamSamplesMetadataJson = {
    format: ADAM_SAMPLES_FORMAT,
    version: ADAM_SAMPLES_PACKAGE_VERSION,
    savedAt: new Date().toISOString(),
    generator: "adam",
    sampleCount: samples.length,
    folderCount: folders.length,
  };

  const entries: Record<string, Uint8Array> = {
    [ADAM_SAMPLES_METADATA_JSON_PATH]: strToU8(stableJsonStringify(metadata)),
    [ADAM_SAMPLES_FOLDERS_JSON_PATH]: strToU8(stableJsonStringify(folders)),
    [ADAM_SAMPLES_MANIFEST_JSON_PATH]: strToU8(
      stableJsonStringify(samples.map(s => s.meta)),
    ),
    [ADAM_SAMPLES_AUDIO_DIR]: new Uint8Array(),
  };

  for (const sample of samples) {
    entries[audioAssetPath(sample.meta.id)] = encodeWavMono16(
      sample.pcm,
      sample.meta.sampleRate,
    );
  }

  return zipSync(entries, { level: 6 });
}

function parseFolders(raw: unknown): SampleFolderMeta[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (f): f is SampleFolderMeta =>
      Boolean(f && typeof f === "object" && typeof (f as SampleFolderMeta).id === "string"),
  );
}

function parseManifest(raw: unknown): SampleAssetMeta[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (m): m is SampleAssetMeta =>
      Boolean(m && typeof m === "object" && typeof (m as SampleAssetMeta).id === "string"),
  );
}

/** Unpack `.adamsamples` zip bytes. */
export function deserializeAdamSamplesPackage(bytes: Uint8Array): AdamSamplesPackage {
  let entries: Record<string, Uint8Array>;
  try {
    entries = unzipSync(bytes);
  } catch {
    throw new Error("Not a valid .adamsamples zip package");
  }

  const metaEntry = entries[ADAM_SAMPLES_METADATA_JSON_PATH];
  if (!metaEntry) {
    throw new Error(`.adamsamples package is missing ${ADAM_SAMPLES_METADATA_JSON_PATH}`);
  }

  let metadata: AdamSamplesMetadataJson;
  try {
    metadata = JSON.parse(strFromU8(metaEntry)) as AdamSamplesMetadataJson;
  } catch {
    throw new Error(`Corrupt ${ADAM_SAMPLES_METADATA_JSON_PATH}`);
  }

  if (metadata.format !== ADAM_SAMPLES_FORMAT) {
    throw new Error(
      `Expected format "${ADAM_SAMPLES_FORMAT}", got ${String(metadata.format)}`,
    );
  }
  if (metadata.version !== ADAM_SAMPLES_PACKAGE_VERSION) {
    throw new Error(`Unsupported .adamsamples version: ${String(metadata.version)}`);
  }

  let folders: SampleFolderMeta[] = [];
  const foldersEntry = entries[ADAM_SAMPLES_FOLDERS_JSON_PATH];
  if (foldersEntry) {
    try {
      folders = parseFolders(JSON.parse(strFromU8(foldersEntry)));
    } catch {
      folders = [];
    }
  }

  let metas: SampleAssetMeta[] = [];
  const manifestEntry = entries[ADAM_SAMPLES_MANIFEST_JSON_PATH];
  if (manifestEntry) {
    try {
      metas = parseManifest(JSON.parse(strFromU8(manifestEntry)));
    } catch {
      metas = [];
    }
  }

  const samples: AdamSamplesPackedSample[] = [];
  for (const meta of metas) {
    const wav = entries[audioAssetPath(meta.id)];
    if (!wav) {
      continue;
    }
    try {
      const decoded = decodeWavToMono(wav);
      samples.push({
        meta: {
          ...meta,
          sampleRate: meta.sampleRate || decoded.sampleRate,
          lengthFrames: meta.lengthFrames || decoded.pcm.length,
          channels: 1,
        },
        pcm: decoded.pcm,
      });
    } catch {
      // skip corrupt wav
    }
  }

  // Fallback: wav files without manifest entries.
  if (samples.length === 0) {
    for (const key of Object.keys(entries)) {
      const match = /^samples\/(.+)\.wav$/.exec(key);
      if (!match) {
        continue;
      }
      const id = match[1]!;
      try {
        const decoded = decodeWavToMono(entries[key]!);
        samples.push({
          meta: {
            id,
            name: id,
            sampleRate: decoded.sampleRate,
            channels: 1,
            lengthFrames: decoded.pcm.length,
            source: "import",
            createdAt: new Date().toISOString(),
          },
          pcm: decoded.pcm,
        });
      } catch {
        // skip
      }
    }
  }

  return {
    metadata: {
      ...metadata,
      sampleCount: samples.length,
      folderCount: folders.length,
    },
    folders,
    samples,
  };
}

/** Pick a `.adamsamples` library package. */
export function pickAdamSamplesFile(): Promise<{ bytes: Uint8Array; name: string }> {
  return new Promise((resolve, reject) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      ".adamsamples,application/vnd.adam.samples+zip,application/zip";
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) {
        reject(new Error("No file selected"));
        return;
      }
      try {
        const buffer = await file.arrayBuffer();
        resolve({ bytes: new Uint8Array(buffer), name: file.name });
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
    input.click();
  });
}
